import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetOne, useRefresh } from 'react-admin';
import type { Contact, Conversation } from '../../../types/chat.types';

export interface ConversationsPageState {
  selectedConversationId: number | null;
  selectedConversation: Conversation | null;
  isLoadingConversation: boolean;
  isDetailsPanelOpen: boolean;
  isConversationListOpen: boolean;
  selectConversation: (conversationId: number | null) => void;
  clearSelectionAndRefresh: () => void;
  toggleDetailsPanel: () => void;
  toggleConversationList: () => void;
  closeDetailsPanel: () => void;
  openDetailsPanel: () => void;
}

/**
 * Hook for Conversations Page state management.
 * Similar to useTicketsPage but conversation-centric instead of ticket-centric.
 * Uses ?conversation= URL param instead of ?ticket=.
 */
export function useConversationsPage(): ConversationsPageState {
  const [searchParams, setSearchParams] = useSearchParams();
  const refresh = useRefresh();
  const conversationIdParam = searchParams.get('conversation');
  const conversationId = conversationIdParam ? Number(conversationIdParam) : null;

  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState<boolean>(false);
  const [isConversationListOpen, setIsConversationListOpen] = useState<boolean>(true);

  // Fetch conversation data
  const {
    data: rawConversation,
    isLoading: isLoadingConversation,
  } = useGetOne<Conversation>(
    'conversation',
    { id: conversationId ?? '' },
    { enabled: !!conversationId }
  );

  // Fetch full contact data to ensure avatarFileId is available
  const contactId = rawConversation?.contactId ?? null;
  const { data: fullContact } = useGetOne<Contact>(
    'contact',
    { id: contactId ?? '' },
    { enabled: !!contactId }
  );

  // Enrich conversation with full contact data
  const selectedConversation = rawConversation && fullContact
    ? { ...rawConversation, contact: { ...rawConversation.contact, ...fullContact } }
    : rawConversation ?? null;

  const selectConversation = useCallback(
    (newConversationId: number | null) => {
      if (newConversationId) {
        setSearchParams({ conversation: String(newConversationId) });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams]
  );

  const clearSelectionAndRefresh = useCallback(() => {
    setSearchParams({});
    setTimeout(() => {
      refresh();
    }, 100);
  }, [setSearchParams, refresh]);

  const toggleDetailsPanel = useCallback(() => {
    setIsDetailsPanelOpen((prev) => !prev);
  }, []);

  const closeDetailsPanel = useCallback(() => {
    setIsDetailsPanelOpen(false);
  }, []);

  const openDetailsPanel = useCallback(() => {
    setIsDetailsPanelOpen(true);
  }, []);

  const toggleConversationList = useCallback(() => {
    setIsConversationListOpen((prev) => !prev);
  }, []);

  // Auto-hide conversation list on mobile when a conversation is selected
  useEffect(() => {
    if (conversationId) {
      const isMobile = window.matchMedia('(max-width: 900px)').matches;
      if (isMobile) {
        setIsConversationListOpen(false);
      }
    }
  }, [conversationId]);

  return {
    selectedConversationId: conversationId,
    selectedConversation: selectedConversation ?? null,
    isLoadingConversation,
    isDetailsPanelOpen,
    isConversationListOpen,
    selectConversation,
    clearSelectionAndRefresh,
    toggleDetailsPanel,
    toggleConversationList,
    closeDetailsPanel,
    openDetailsPanel,
  };
}
